import type { ISerialize } from '@/objects/ISerialize';
import type { WeatherTypes } from './../enums/WeatherTypes';
import { formatDateIso } from '@/utilities/dateutils';

export class WeatherData implements ISerialize {
    public AirPressureAtSeaLevel: number;

    public AirTemperature: number;

    public AirTemperaturePercentile10: number;

    public AirTemperaturePercentile90: number;

    public BetweenSunsetSunrise: boolean;

    public CloudAreaFraction: number;

    public CloudAreaFractionHigh: number;

    public CloudAreaFractionLow: number;

    public CloudAreaFractionMedium: number;

    public DewPointTemperature: number;

    public FogAreaFraction: number;

    public PrecipitationAmount: number;

    public PrecipitationAmountMax: number;

    public PrecipitationAmountMin: number;

    public ProbabilityOfPrecipitation: number;

    public ProbabilityOfThunder: number;

    public RelativeHumidity: number;

    public Symbol: WeatherTypes | null;

    public Timestamp: Date;

    public UltravioletIndexClearSky: number;

    public WindFromDirection: number;

    public WindSpeed: number;

    public WindSpeedOfGust: number;

    public WindSpeedPercentile10: number;

    public WindSpeedPercentile90: number;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AirPressureAtSeaLevel?: number;
        AirTemperature?: number;
        AirTemperaturePercentile10?: number;
        AirTemperaturePercentile90?: number;
        BetweenSunsetSunrise?: boolean;
        CloudAreaFraction?: number;
        CloudAreaFractionHigh?: number;
        CloudAreaFractionLow?: number;
        CloudAreaFractionMedium?: number;
        DewPointTemperature?: number;
        FogAreaFraction?: number;
        PrecipitationAmount?: number;
        PrecipitationAmountMax?: number;
        PrecipitationAmountMin?: number;
        ProbabilityOfPrecipitation?: number;
        ProbabilityOfThunder?: number;
        RelativeHumidity?: number;
        Symbol?: WeatherTypes | null;
        Timestamp?: Date;
        UltravioletIndexClearSky?: number;
        WindFromDirection?: number;
        WindSpeed?: number;
        WindSpeedOfGust?: number;
        WindSpeedPercentile10?: number;
        WindSpeedPercentile90?: number;
    }) {
        this.AirPressureAtSeaLevel = values.AirPressureAtSeaLevel ?? 0;

        this.AirTemperature = values.AirTemperature ?? 0;

        this.AirTemperaturePercentile10 = values.AirTemperaturePercentile10 ?? 0;

        this.AirTemperaturePercentile90 = values.AirTemperaturePercentile90 ?? 0;

        this.BetweenSunsetSunrise = values.BetweenSunsetSunrise ?? false;

        this.CloudAreaFraction = values.CloudAreaFraction ?? 0;

        this.CloudAreaFractionHigh = values.CloudAreaFractionHigh ?? 0;

        this.CloudAreaFractionLow = values.CloudAreaFractionLow ?? 0;

        this.CloudAreaFractionMedium = values.CloudAreaFractionMedium ?? 0;

        this.DewPointTemperature = values.DewPointTemperature ?? 0;

        this.FogAreaFraction = values.FogAreaFraction ?? 0;

        this.PrecipitationAmount = values.PrecipitationAmount ?? 0;

        this.PrecipitationAmountMax = values.PrecipitationAmountMax ?? 0;

        this.PrecipitationAmountMin = values.PrecipitationAmountMin ?? 0;

        this.ProbabilityOfPrecipitation = values.ProbabilityOfPrecipitation ?? 0;

        this.ProbabilityOfThunder = values.ProbabilityOfThunder ?? 0;

        this.RelativeHumidity = values.RelativeHumidity ?? 0;

        this.Symbol = values.Symbol ?? 'clearsky';

        this.Timestamp = values.Timestamp ?? new Date('1970-01-01T00:00:00.000');

        this.UltravioletIndexClearSky = values.UltravioletIndexClearSky ?? 0;

        this.WindFromDirection = values.WindFromDirection ?? 0;

        this.WindSpeed = values.WindSpeed ?? 0;

        this.WindSpeedOfGust = values.WindSpeedOfGust ?? 0;

        this.WindSpeedPercentile10 = values.WindSpeedPercentile10 ?? 0;

        this.WindSpeedPercentile90 = values.WindSpeedPercentile90 ?? 0;
    }

    public serialize(): string {
        return (
            '{' +
            `"AirPressureAtSeaLevel": ${JSON.stringify(this.AirPressureAtSeaLevel)}` +
            ',' +
            `"AirTemperature": ${JSON.stringify(this.AirTemperature)}` +
            ',' +
            `"AirTemperaturePercentile10": ${JSON.stringify(this.AirTemperaturePercentile10)}` +
            ',' +
            `"AirTemperaturePercentile90": ${JSON.stringify(this.AirTemperaturePercentile90)}` +
            ',' +
            `"BetweenSunsetSunrise": ${JSON.stringify(this.BetweenSunsetSunrise)}` +
            ',' +
            `"CloudAreaFraction": ${JSON.stringify(this.CloudAreaFraction)}` +
            ',' +
            `"CloudAreaFractionHigh": ${JSON.stringify(this.CloudAreaFractionHigh)}` +
            ',' +
            `"CloudAreaFractionLow": ${JSON.stringify(this.CloudAreaFractionLow)}` +
            ',' +
            `"CloudAreaFractionMedium": ${JSON.stringify(this.CloudAreaFractionMedium)}` +
            ',' +
            `"DewPointTemperature": ${JSON.stringify(this.DewPointTemperature)}` +
            ',' +
            `"FogAreaFraction": ${JSON.stringify(this.FogAreaFraction)}` +
            ',' +
            `"PrecipitationAmount": ${JSON.stringify(this.PrecipitationAmount)}` +
            ',' +
            `"PrecipitationAmountMax": ${JSON.stringify(this.PrecipitationAmountMax)}` +
            ',' +
            `"PrecipitationAmountMin": ${JSON.stringify(this.PrecipitationAmountMin)}` +
            ',' +
            `"ProbabilityOfPrecipitation": ${JSON.stringify(this.ProbabilityOfPrecipitation)}` +
            ',' +
            `"ProbabilityOfThunder": ${JSON.stringify(this.ProbabilityOfThunder)}` +
            ',' +
            `"RelativeHumidity": ${JSON.stringify(this.RelativeHumidity)}` +
            ',' +
            `"Symbol": ${this.Symbol == null ? null : this.Symbol == 'clearsky' ? 0 : this.Symbol == 'fair' ? 1 : this.Symbol == 'partlycloudy' ? 2 : this.Symbol == 'cloudy' ? 3 : this.Symbol == 'lightrainshowers' ? 4 : this.Symbol == 'rainshowers' ? 5 : this.Symbol == 'heavyrainshowers' ? 6 : this.Symbol == 'lightrainshowersandthunder' ? 7 : this.Symbol == 'rainshowersandthunder' ? 8 : this.Symbol == 'heavyrainshowersandthunder' ? 9 : this.Symbol == 'lightsleetshowers' ? 10 : this.Symbol == 'sleetshowers' ? 11 : this.Symbol == 'heavysleetshowers' ? 12 : this.Symbol == 'lightssleetshowersandthunder' ? 13 : this.Symbol == 'sleetshowersandthunder' ? 14 : this.Symbol == 'heavysleetshowersandthunder' ? 15 : this.Symbol == 'lightsnowshowers' ? 16 : this.Symbol == 'snowshowers' ? 17 : this.Symbol == 'heavysnowshowers' ? 18 : this.Symbol == 'lightssnowshowersandthunder' ? 19 : this.Symbol == 'snowshowersandthunder' ? 20 : this.Symbol == 'heavysnowshowersandthunder' ? 21 : this.Symbol == 'lightrain' ? 22 : this.Symbol == 'rain' ? 23 : this.Symbol == 'heavyrain' ? 24 : this.Symbol == 'lightrainandthunder' ? 25 : this.Symbol == 'rainandthunder' ? 26 : this.Symbol == 'heavyrainandthunder' ? 27 : this.Symbol == 'lightsleet' ? 28 : this.Symbol == 'sleet' ? 29 : this.Symbol == 'heavysleet' ? 30 : this.Symbol == 'lightsleetandthunder' ? 31 : this.Symbol == 'sleetandthunder' ? 32 : this.Symbol == 'heavysleetandthunder' ? 33 : this.Symbol == 'lightsnow' ? 34 : this.Symbol == 'snow' ? 35 : this.Symbol == 'heavysnow' ? 36 : this.Symbol == 'lightsnowandthunder' ? 37 : this.Symbol == 'snowandthunder' ? 38 : this.Symbol == 'heavysnowandthunder' ? 39 : this.Symbol == 'fog' ? 40 : null}` +
            ',' +
            `"Timestamp": ${'"' + formatDateIso(this.Timestamp) + '"'}` +
            ',' +
            `"UltravioletIndexClearSky": ${JSON.stringify(this.UltravioletIndexClearSky)}` +
            ',' +
            `"WindFromDirection": ${JSON.stringify(this.WindFromDirection)}` +
            ',' +
            `"WindSpeed": ${JSON.stringify(this.WindSpeed)}` +
            ',' +
            `"WindSpeedOfGust": ${JSON.stringify(this.WindSpeedOfGust)}` +
            ',' +
            `"WindSpeedPercentile10": ${JSON.stringify(this.WindSpeedPercentile10)}` +
            ',' +
            `"WindSpeedPercentile90": ${JSON.stringify(this.WindSpeedPercentile90)}` +
            '}'
        );
    }

    /**
     * Creates an instance of WeatherData from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AirPressureAtSeaLevel?: number;
        AirTemperature?: number;
        AirTemperaturePercentile10?: number;
        AirTemperaturePercentile90?: number;
        BetweenSunsetSunrise?: boolean;
        CloudAreaFraction?: number;
        CloudAreaFractionHigh?: number;
        CloudAreaFractionLow?: number;
        CloudAreaFractionMedium?: number;
        DewPointTemperature?: number;
        FogAreaFraction?: number;
        PrecipitationAmount?: number;
        PrecipitationAmountMax?: number;
        PrecipitationAmountMin?: number;
        ProbabilityOfPrecipitation?: number;
        ProbabilityOfThunder?: number;
        RelativeHumidity?: number;
        Symbol?: WeatherTypes | null;
        Timestamp?: Date;
        UltravioletIndexClearSky?: number;
        WindFromDirection?: number;
        WindSpeed?: number;
        WindSpeedOfGust?: number;
        WindSpeedPercentile10?: number;
        WindSpeedPercentile90?: number;
    }): WeatherData {
        return new WeatherData(values);
    }

    public static _FromServerJson(json: any): WeatherData {
        return new WeatherData({
            AirPressureAtSeaLevel: json.AirPressureAtSeaLevel,
            AirTemperature: json.AirTemperature,
            AirTemperaturePercentile10: json.AirTemperaturePercentile10,
            AirTemperaturePercentile90: json.AirTemperaturePercentile90,
            BetweenSunsetSunrise: json.BetweenSunsetSunrise,
            CloudAreaFraction: json.CloudAreaFraction,
            CloudAreaFractionHigh: json.CloudAreaFractionHigh,
            CloudAreaFractionLow: json.CloudAreaFractionLow,
            CloudAreaFractionMedium: json.CloudAreaFractionMedium,
            DewPointTemperature: json.DewPointTemperature,
            FogAreaFraction: json.FogAreaFraction,
            PrecipitationAmount: json.PrecipitationAmount,
            PrecipitationAmountMax: json.PrecipitationAmountMax,
            PrecipitationAmountMin: json.PrecipitationAmountMin,
            ProbabilityOfPrecipitation: json.ProbabilityOfPrecipitation,
            ProbabilityOfThunder: json.ProbabilityOfThunder,
            RelativeHumidity: json.RelativeHumidity,
            Symbol:
                json.Symbol == null
                    ? null
                    : json.Symbol == 0
                      ? 'clearsky'
                      : json.Symbol == 1
                        ? 'fair'
                        : json.Symbol == 2
                          ? 'partlycloudy'
                          : json.Symbol == 3
                            ? 'cloudy'
                            : json.Symbol == 4
                              ? 'lightrainshowers'
                              : json.Symbol == 5
                                ? 'rainshowers'
                                : json.Symbol == 6
                                  ? 'heavyrainshowers'
                                  : json.Symbol == 7
                                    ? 'lightrainshowersandthunder'
                                    : json.Symbol == 8
                                      ? 'rainshowersandthunder'
                                      : json.Symbol == 9
                                        ? 'heavyrainshowersandthunder'
                                        : json.Symbol == 10
                                          ? 'lightsleetshowers'
                                          : json.Symbol == 11
                                            ? 'sleetshowers'
                                            : json.Symbol == 12
                                              ? 'heavysleetshowers'
                                              : json.Symbol == 13
                                                ? 'lightssleetshowersandthunder'
                                                : json.Symbol == 14
                                                  ? 'sleetshowersandthunder'
                                                  : json.Symbol == 15
                                                    ? 'heavysleetshowersandthunder'
                                                    : json.Symbol == 16
                                                      ? 'lightsnowshowers'
                                                      : json.Symbol == 17
                                                        ? 'snowshowers'
                                                        : json.Symbol == 18
                                                          ? 'heavysnowshowers'
                                                          : json.Symbol == 19
                                                            ? 'lightssnowshowersandthunder'
                                                            : json.Symbol == 20
                                                              ? 'snowshowersandthunder'
                                                              : json.Symbol == 21
                                                                ? 'heavysnowshowersandthunder'
                                                                : json.Symbol == 22
                                                                  ? 'lightrain'
                                                                  : json.Symbol == 23
                                                                    ? 'rain'
                                                                    : json.Symbol == 24
                                                                      ? 'heavyrain'
                                                                      : json.Symbol == 25
                                                                        ? 'lightrainandthunder'
                                                                        : json.Symbol == 26
                                                                          ? 'rainandthunder'
                                                                          : json.Symbol == 27
                                                                            ? 'heavyrainandthunder'
                                                                            : json.Symbol == 28
                                                                              ? 'lightsleet'
                                                                              : json.Symbol == 29
                                                                                ? 'sleet'
                                                                                : json.Symbol == 30
                                                                                  ? 'heavysleet'
                                                                                  : json.Symbol == 31
                                                                                    ? 'lightsleetandthunder'
                                                                                    : json.Symbol == 32
                                                                                      ? 'sleetandthunder'
                                                                                      : json.Symbol == 33
                                                                                        ? 'heavysleetandthunder'
                                                                                        : json.Symbol == 34
                                                                                          ? 'lightsnow'
                                                                                          : json.Symbol == 35
                                                                                            ? 'snow'
                                                                                            : json.Symbol == 36
                                                                                              ? 'heavysnow'
                                                                                              : json.Symbol == 37
                                                                                                ? 'lightsnowandthunder'
                                                                                                : json.Symbol == 38
                                                                                                  ? 'snowandthunder'
                                                                                                  : json.Symbol == 39
                                                                                                    ? 'heavysnowandthunder'
                                                                                                    : json.Symbol == 40
                                                                                                      ? 'fog'
                                                                                                      : null,
            Timestamp: json.Timestamp instanceof Date ? json.Timestamp : new Date(json.Timestamp),
            UltravioletIndexClearSky: json.UltravioletIndexClearSky,
            WindFromDirection: json.WindFromDirection,
            WindSpeed: json.WindSpeed,
            WindSpeedOfGust: json.WindSpeedOfGust,
            WindSpeedPercentile10: json.WindSpeedPercentile10,
            WindSpeedPercentile90: json.WindSpeedPercentile90,
        });
    }

    /**
     * Creates an instance of WeatherData from another instance of WeatherData.
     * @param other The WeatherData you want to clone
     * @returns A cloned WeatherData
     */
    public static FromOther(other: WeatherData): WeatherData {
        return WeatherData._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return WeatherData.FromOther(this);
    }

    static NAME_OF_CLASS = 'WeatherData';
}
